// src/presentation/pages/ClinicalExamination/sections/HeadInjury.tsx
import React from "react";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";

interface HeadInjuryProps {
  value: boolean | null;
  lossOfConsciousness: boolean | null;
  year: string;
  onChange: (value: boolean) => void;
  onLossOfConsciousnessChange: (value: boolean) => void;
  onYearChange: (value: string) => void;
}

export const HeadInjury: React.FC<HeadInjuryProps> = ({
  value,
  lossOfConsciousness,
  year,
  onChange,
  onLossOfConsciousnessChange,
  onYearChange,
}) => {
  const { t } = useTranslation("patients");
  const showFields = value === true;

  return (
    <div className="border-gray-200">
      <CardHeader className="pb-3 !px-0">
        <CardTitle className="text-base font-semibold text-gray-900">
          {t("clinicalSections.headInjury.title")}
        </CardTitle>
        <p className="text-sm text-gray-500">
          {t("clinicalSections.headInjury.question")}
        </p>
      </CardHeader>
      <CardContent className="space-y-4 !px-0">
        <RadioGroup
          value={value?.toString()}
          onValueChange={(val) => onChange(val === "true")}
          className="flex gap-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="true" id="head-yes" />
            <Label
              htmlFor="head-yes"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.yes")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="false" id="head-no" />
            <Label
              htmlFor="head-no"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.no")}
            </Label>
          </div>
        </RadioGroup>

        {showFields && (
          <div className="space-y-4   border-primary/30">
            <div>
              <p className="text-sm text-gray-700 mb-3">
                {t("clinicalSections.headInjury.lossOfConsciousnessQuestion")}
              </p>
              <RadioGroup
                value={lossOfConsciousness?.toString()}
                onValueChange={(val) =>
                  onLossOfConsciousnessChange(val === "true")
                }
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="loc-yes" />
                  <Label
                    htmlFor="loc-yes"
                    className="text-[#1B1D21] cursor-pointer text-sm font-normal"
                  >
                    {t("clinicalSections.yes")}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="loc-no" />
                  <Label
                    htmlFor="loc-no"
                    className="text-[#1B1D21] cursor-pointer text-sm font-normal"
                  >
                    {t("clinicalSections.no")}
                  </Label>
                </div>
              </RadioGroup>
            </div>
            <div>
              <Label
                htmlFor="head-injury-year"
                className="text-sm text-[#5F646F]"
              >
                {t("clinicalSections.headInjury.yearLabel")}
              </Label>
              <Input
                id="head-injury-year"
                type="text"
                inputMode="numeric"
                placeholder="YYYY"
                value={year}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                  onYearChange(v);
                }}
                className="mt-1 w-40"
              />
            </div>
          </div>
        )}
      </CardContent>
    </div>
  );
};
